import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getResidentialUnitsByProjectIdWithExtraData } from '@/services/residential-units';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import ResidentialUnitList, { ResidentialUnitViewMode } from '@/seller/components/residential-units/ResidentialUnitList';
import { ResidentialUnitExtraData } from '@/types';
import { IState } from '@/state/IState';
import { ButtonBar, Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import ProjectResidentialUnitFilter from '../../residential-units/ResidentialUnitsFilter/ResidentialUnitsFilter';
import { useAsyncState, useQueryState, useParamReplace } from '@/utils/hooks';
import { orderBy } from 'lodash-es';
import { ResidentialUnitFilterOptions } from '@/seller/components/residential-units/ResidentialUnitsFilter';
import { useParams } from 'react-router';
import { useUnreadMessagesByUnit } from '.';
import { NoResultsCard } from '@/components/Cards';

const soldStatusToBoolean = (status: ResidentialUnitFilterOptions['soldStatus']) => {
    switch (status) {
        case 'sold':
            return true;
        case 'unsold':
            return false;
        default:
            return null;
    }
};

interface ProjectResidentialUnitsProps {}

const ResidentialUnitsSorted: React.FC<ProjectResidentialUnitsProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [filter, setFilter] = useQueryState<ResidentialUnitFilterOptions>(ResidentialUnitFilterOptions.empty());
    const [residentialUnits, , reloadResidentialUnits] = useAsyncState<ResidentialUnitExtraData[] | null>(
        () =>
            getResidentialUnitsByProjectIdWithExtraData(projectId, {
                floors: filter.floors,
                layoutTypes: filter.layoutTypes,
                tagIds: filter.tags,
                roomIds: filter.rooms,
                groupIds: filter.groups,
                searchText: filter.searchText,
                sold: soldStatusToBoolean(filter.soldStatus),
            }),
        [filter, projectId],
        null
    );

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsLoading(true);
    }, [filter]);

    useEffect(() => {
        setIsLoading(false);
    }, [residentialUnits]);

    const handleUnitUpdate = useCallback(() => {
        reloadResidentialUnits();
    }, [reloadResidentialUnits]);

    const handleDelete = useCallback(() => {
        reloadResidentialUnits();
    }, [reloadResidentialUnits]);

    const { viewMode } = useParams<{ viewMode: ResidentialUnitViewMode }>();
    const replaceParams = useParamReplace<{ viewMode?: ResidentialUnitViewMode }>();

    const [, getUnitThreadInfo] = useUnreadMessagesByUnit();
    const getNotificationCount = (residentialUnitId: number) => getUnitThreadInfo(residentialUnitId)?.unreadThreads ?? 0;

    const hrefGenerator = useCallback((ru) => `/residential-units/${ru.id}`, []);

    if (!residentialUnits) {
        return <LoadingIndicator />;
    }

    const renderUnits = () => {
        const sortedUnits = orderBy(
            residentialUnits,
            [
                (unit) => (getUnitThreadInfo(unit.id)?.unreadThreads ?? 0) > 0,
                (unit) => getUnitThreadInfo(unit.id)?.lastMessageDate ?? new Date(1970, 0, 1),
                'name',
            ],
            ['desc', 'desc', 'asc']
        );

        if (sortedUnits.length === 0) {
            return (
                <NoResultsCard>
                    <Trans>No units</Trans>
                </NoResultsCard>
            );
        }

        return (
            <ResidentialUnitList
                onDelete={handleDelete}
                onUpdate={handleUnitUpdate}
                residentialUnits={sortedUnits}
                hrefGenerator={hrefGenerator}
                viewMode={viewMode}
                notificationCountGetter={getNotificationCount}
            />
        );
    };

    return (
        <>
            <ButtonBar sticky stickyClassName="stickyBleed">
                <Tabs activeId={viewMode} variant="pill" size="sm">
                    <Tabs.Tab
                        id={ResidentialUnitViewMode.Compressed}
                        as={Link}
                        to={replaceParams({ viewMode: ResidentialUnitViewMode.Compressed })}
                        label={<Trans>Compressed view</Trans>}
                    />
                    <Tabs.Tab
                        id={ResidentialUnitViewMode.Expanded}
                        as={Link}
                        to={replaceParams({ viewMode: ResidentialUnitViewMode.Expanded })}
                        label={<Trans>Expanded view</Trans>}
                    />
                </Tabs>

                <ButtonBar.Right>
                    <ProjectResidentialUnitFilter isLoading={isLoading} filter={filter} onFilterChange={setFilter} />
                </ButtonBar.Right>
            </ButtonBar>

            {renderUnits()}
        </>
    );
};
export default ResidentialUnitsSorted;
